/**
 * ── A BANDEIRA DA LÍNGUA ───────────────────────────────────────────────────
 *
 * Ordem do dono (24/10/2026): *«eu quero que na area de membros fique a
 * bandeira da lingua pra escolher. e nao o nome»*.
 *
 * ## Por que desenhada, e não um emoji
 *
 * `🇧🇷` é um par de indicadores regionais, e **o Windows não os compõe**: o
 * Chrome no Windows desenha as duas letras `BR`. Um seletor de bandeiras que
 * vira um seletor de siglas na plataforma onde o dono testa não é um seletor
 * de bandeiras. O desenho sai igual em toda parte e custa os bytes abaixo.
 *
 * ## Por que PATH e não `<rect>` / `<circle>`
 *
 * Não é preciosismo de estilo: é o orçamento. `d="M0 0h3v2H0z"` são 11 bytes
 * contra os 34 de `x="0" y="0" width="3" height="2"`, e o pedaço de entrada
 * do aluno tinha **1.278 B** de folga quando isto foi escrito (§20 da folha em
 * `comunidade/o-editor-nao-entra-na-porta.test.ts`). Por isso cada bandeira é
 * uma lista de `[cor, traçado]` e não seis componentes.
 *
 * Pela mesma conta, a borda e o canto são **classe de CSS** e não traçado: o
 * CSS sai no `.css` do artefacto, que a cerca dos bytes não pesa.
 *
 * ## O que elas NÃO são
 *
 * Uma bandeira é de um país, não de uma língua — um mexicano não é a bandeira
 * de Espanha. O que torna isto honesto aqui é que o catálogo desta casa já é
 * **com região** por decisão escrita (`es-ES` chama-se *Español (España)*, e a
 * razão está em `player-legendas.ts`): cada entrada nomeia mesmo um país. E a
 * bandeira nunca é o único portador do sentido — o botão que a embrulha leva o
 * endónimo em `aria-label` e em `title`, logo o leitor de tela e o ponteiro
 * continuam a ouvir e a ler «Español (España)».
 *
 * ## A sétima língua
 *
 * Entrar no catálogo e não ter desenho daria um botão VAZIO — um controle
 * invisível, que é o defeito `L5`/`L11` que esta casa já pagou uma vez. Por
 * isso o `else` desenha a sigla, e por isso existe a cerca
 * `a-bandeira-cobre-o-catalogo.test.tsx`, que lê as duas listas e reprova
 * quando elas divergem.
 */

/** `[cor, traçado]`, em cima de uma tela de `3 × 2`, na ordem de pintura. */
const DESENHOS: Record<string, readonly (readonly [string, string])[]> = {
  'pt-BR': [
    ['#009B3A', 'M0 0h3v2H0z'],
    ['#FEDF00', 'm1.5.25 1.2.75-1.2.75L.3 1z'],
    ['#002776', 'M1.9 1A.4.4 0 1 1 1.1 1 .4.4 0 1 1 1.9 1z'],
  ],
  'en-US': [
    ['#B22234', 'M0 0h3v2H0z'],
    [
      '#fff',
      'M0 .15h3v.15H0zM0 .45h3v.15H0zM0 .75h3v.15H0zM0 1.05h3v.15H0zM0 1.35h3v.15H0zM0 1.65h3v.15H0z',
    ],
    ['#3C3B6E', 'M0 0h1.2v1.05H0z'],
  ],
  'es-ES': [
    ['#AA151B', 'M0 0h3v2H0z'],
    ['#F1BF00', 'M0 .5h3v1H0z'],
  ],
  'fr-FR': [
    ['#002654', 'M0 0h1v2H0z'],
    ['#fff', 'M1 0h1v2H1z'],
    ['#CE1126', 'M2 0h1v2H2z'],
  ],
  'de-DE': [
    ['#D00', 'M0 0h3v2H0z'],
    ['#000', 'M0 0h3v.667H0z'],
    ['#FFCE00', 'M0 1.333h3v.667H0z'],
  ],
  'it-IT': [
    ['#008C45', 'M0 0h1v2H0z'],
    ['#fff', 'M1 0h1v2H1z'],
    ['#CD212A', 'M2 0h1v2H2z'],
  ],
};

/** Que códigos têm desenho — lido pela cerca que os cruza com o catálogo. */
export const IDIOMAS_COM_BANDEIRA = Object.keys(DESENHOS);

/**
 * O `ring` existe para a bandeira de bordo branco (França, Itália) não se
 * dissolver no fundo claro da área. `aria-hidden` porque quem diz o nome é o
 * botão: duas vozes para a mesma coisa é o leitor de tela a repetir-se.
 */
export function Bandeira({ codigo }: { codigo: string }) {
  const desenho = DESENHOS[codigo];
  const classe = 'rounded-[2px] ring-1 ring-black/20';

  if (!desenho) {
    /* Sem desenho, a SIGLA — nunca nada. Ver «A sétima língua», acima. */
    return (
      <span aria-hidden className={`${classe} px-1 text-[10px] font-semibold uppercase`}>
        {codigo.slice(0, 2)}
      </span>
    );
  }

  return (
    <svg aria-hidden focusable="false" viewBox="0 0 3 2" width="24" height="16" className={classe}>
      {desenho.map(([cor, d]) => (
        <path key={d} fill={cor} d={d} />
      ))}
    </svg>
  );
}
